import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import { spLogTags, spWarnTags } from "@eventr/common";
import { bs } from "../../../state/bound.store";
import PushDiagnostics from "./plugin";
import { findAnomalies, groupByNotification, parseEntries, summariseTrail } from "./trail";

export { default as PushDiagnostics } from "./plugin";

/**
 * Collects the push trail both processes have been writing since the last drain and
 * reports it to Sentry.
 *
 * Called on app resume: the notification service extension runs whether or not the app
 * is alive, so the app can only ever read the trail after the fact. Anything genuinely
 * urgent is in the anomaly list, which is escalated to a warning event.
 */
export async function drainPushDiagnostics() {
  if (Capacitor.getPlatform() !== "ios") {
    return;
  }

  // Web assets ship ahead of the binary through CapacitorUpdater, so a device can be
  // running this code on a build with no native side. Returning quietly beats logging
  // a miss on every resume, which is what the log this replaced used to do.
  if (!Capacitor.isPluginAvailable("PushDiagnostics")) {
    return;
  }

  try {
    const { entries: lines } = await PushDiagnostics.drain();
    if (!lines.length) {
      return;
    }

    const trails = groupByNotification(parseEntries(lines));
    if (!trails.length) {
      return;
    }

    const anomalies = findAnomalies(trails);
    const summaries = trails.map(summariseTrail);

    for (const summary of summaries) {
      Sentry.addBreadcrumb({ category: "push.trail", level: "info", message: summary });
    }

    if (anomalies.length) {
      spWarnTags("Push delivery anomalies", ["sentry", "push"], anomalies);
    } else {
      spLogTags(`Push trails drained: ${trails.length}`, ["sentry", "push"]);
    }

    // Titles are kept free of counts and ids so Sentry groups these rather than
    // opening a fresh issue per resume; the detail lives in tags and extra.
    Sentry.captureMessage(
      anomalies.length
        ? `Push delivery anomalies: ${[...new Set(anomalies.map((a) => a.kind))].sort().join(", ")}`
        : "Push delivery trail",
      {
        level: anomalies.length ? "warning" : "info",
        user: { email: bs()?.wpUser?.email },
        tags: { push_anomaly: anomalies[0]?.kind ?? "none" },
        extra: { trails: summaries, anomalies, notifications: trails.length, entries: lines.length },
      },
    );
  } catch (error) {
    spLogTags("Could not drain push diagnostics", ["sentry", "push"], error);
  }
}
